import type { components } from "../../api/types";
import {
  LAYER_SETTLED_LABEL,
  WATCHING_HEADING,
  type LayerGroupData,
  type SpecLayer,
} from "../../lib/specLines";
import { stripAnchors } from "../../lib/stripAnchors";
import { AreaGroup, Caret, HEAD_CLASS, HeadTally, SUMMARY_CLASS, foldProps } from "./AreaGroup";
import { Row } from "./Row";

type Node = components["schemas"]["Node"];

const PROSE = "whitespace-pre-wrap break-words text-[14px] leading-relaxed text-ink-200";
const HEADING = "pt-2 font-mono text-[10px] uppercase tracking-widest text-ink-400";

// One layer of the spec column (0051 slice 3). Its head: the layer's name, a ✓
// when it is settled, its one rollup chip and how many rows read it, over the
// rows the active filter keeps (headTally; neither when no row passes).
// Opened: its reflection (the settled summary and what we
// are watching, as stored; never the next-layer preview or the hand-off
// framing), then its areas and the rows no area holds. While a pass is in
// flight it shows neither the ✓ nor the reflection: the pass may reopen the
// layer (0039 N4). A head with nothing to open is not a toggle.
export function LayerGroup({
  layer,
  group,
  open,
  onToggle,
  inFlight,
  openAreas,
  onToggleArea,
  nodeById,
  onOpenRow,
  registerRow,
}: {
  layer: SpecLayer;
  group: LayerGroupData;
  open: boolean;
  onToggle: (open: boolean) => void;
  inFlight: boolean;
  // The open areas, keyed `${ring}:${areaId}`.
  openAreas: ReadonlySet<string>;
  onToggleArea: (key: string, open: boolean) => void;
  nodeById: ReadonlyMap<string, Node>;
  onOpenRow: (id: string) => void;
  registerRow: (id: string, el: HTMLButtonElement | null) => void;
}) {
  const ring = layer.ring;
  const rows = [...group.areas.flatMap((a) => a.rows), ...group.loose];
  // The server serves a reflection only on a complete layer; the column keeps
  // only the in-flight gate.
  const reflection = inFlight ? undefined : layer.reflection;
  const summary = stripAnchors(reflection?.summary ?? "").trim();
  const concerns = stripAnchors(reflection?.concerns ?? "").trim();
  const hasReflection = summary !== "" || concerns !== "";

  const head = (
    <>
      {layer.name.trim() !== "" && (
        <span className="min-w-0 break-words text-[15px] font-medium text-ink-100">{layer.name}</span>
      )}
      {layer.state === "complete" && !inFlight && (
        <span
          data-testid={`layer-settled-${ring}`}
          role="img"
          aria-label={LAYER_SETTLED_LABEL}
          title={LAYER_SETTLED_LABEL}
          className="shrink-0 text-[13px] text-ink-300"
        >
          ✓
        </span>
      )}
      <HeadTally rows={rows} />
    </>
  );

  if (group.areas.length === 0 && group.loose.length === 0 && !hasReflection) {
    return (
      <section data-testid={`layer-${ring}`}>
        <div data-testid={`layer-head-${ring}`} className={HEAD_CLASS}>
          <Caret />
          {head}
        </div>
      </section>
    );
  }

  const fold = foldProps(open, onToggle);
  return (
    <section data-testid={`layer-${ring}`}>
      <details {...fold.details} className="min-w-0">
        <summary data-testid={`layer-head-${ring}`} {...fold.summary} className={SUMMARY_CLASS}>
          <Caret open={open} />
          {head}
        </summary>
        <div className="space-y-1 pb-3 pl-4">
          {hasReflection && (
            <div data-testid={`layer-reflection-${ring}`} className="space-y-1 break-words pb-2">
              {summary !== "" && <p className={PROSE}>{summary}</p>}
              {concerns !== "" && (
                <>
                  <p className={HEADING}>{WATCHING_HEADING}</p>
                  <p className={PROSE}>{concerns}</p>
                </>
              )}
            </div>
          )}
          {group.areas.map((area) => {
            const key = `${ring}:${area.id}`;
            return (
              <AreaGroup
                key={area.id}
                ring={ring}
                area={area}
                open={openAreas.has(key)}
                onToggle={(o) => onToggleArea(key, o)}
                nodeById={nodeById}
                onOpenRow={onOpenRow}
                registerRow={registerRow}
              />
            );
          })}
          {group.loose.map((row) => (
            <Row key={row.node.id} row={row} nodeById={nodeById} onOpen={onOpenRow} register={registerRow} />
          ))}
        </div>
      </details>
    </section>
  );
}
